import {
  Button,
  NewButton,
  NewButtonSize,
  NewTextField,
  TextField,
} from "@bale/ui";
import CardWrapper from "components/commons/cardWrapper/CardWrapper";
import {
  GeneralErrors,
  GeneralValidators,
  useInputHandler,
} from "hooks/useInputHandler";
import { strings } from "intl/fa";
import { useEffect, useMemo, useState } from "react";
import styles from "./SponsoredInfo.module.scss";
import Footer from "components/commons/footer/Footer";
import { useAppDispatch, useAppSelector } from "utils/store";
import { OrderAdActions, OrderAdPages } from "core/orderAd/orderAd.slice";
import {
  LinkErrors,
  validateBaleUrl,
  validateForbiddenPlatforms,
} from "components/content/dialogAd/steps/infoStep/validateInfo";
import { WebUrl } from "utils/regex";
import { countLinks } from "@bale/utils";
import useValidateChannelMembersCount from "hooks/useValidateChannelMembersCount";
import { MIN_MEMBER } from "./constants";
import { toast } from "react-toastify";
import { formatAdUrl } from "utils/utils";

export const SponsoredInfo = () => {
  const dispatch = useAppDispatch();
  const adTitleInputHandler = useInputHandler();
  const [loading, setLoading] = useState<boolean>(false);
  const validateChannelMemberCount = useValidateChannelMembersCount(MIN_MEMBER);
  const { link_title, link, description } = useAppSelector(
    (state) => state.orderAd.info
  );

  const onSubmit = () => {
    setLoading(true);
    validateChannelMemberCount(
      formatAdUrl(buttonLinkInputHandler.rawValue),
      (res, notFound) => {
        setLoading(false);
        if (notFound) {
          toast.info(strings.dialog_ad_form.info_step.errors.no_channel_found, {
            toastId: "not_valid_channel",
          });
          return;
        }

        if (res) {
          dispatch(
            OrderAdActions.setOrderAdInfo({
              link_title: isJoinLink
                ? strings.order.sponsored.info.join
                : buttonTitleInputHandler.rawValue.trim(),
              link: buttonLinkInputHandler.rawValue,
              description: adTextInputHandler.rawValue.trim(),
              name: adTitleInputHandler.rawValue.trim(),
            })
          );
          dispatch(
            OrderAdActions.setPage(OrderAdPages.SponsoredMessageImageStep)
          );
        } else {
          toast.info(strings.sponsored_messages.low_members);
        }
      }
    );
  };

  const buttonTitleInputHandler = useInputHandler({
    initialValue: link_title,
    rules: [
      GeneralValidators.NotAnyEnglish,
      GeneralValidators.required,
      GeneralValidators.maxLength(15),
      GeneralValidators.NotAllEnglish,
    ],
    errorMessages: {
      [GeneralErrors.MaximumLength]: strings.order.sponsored.info.long_title,
      [GeneralErrors.Required]: strings.common.error.required,
      [GeneralErrors.NoAnyEnglish]:
        strings.order.sponsored.info.button_english_error,
    },
    beforeBlur: () => {
      buttonTitleInputHandler.validate();
    },
  });

  const adLinkRules = [
    GeneralValidators.required,
    GeneralValidators.regexMatch(WebUrl),
    validateBaleUrl,
    validateForbiddenPlatforms,
  ];

  const buttonLinkInputHandler = useInputHandler({
    initialValue: link,
    rules: adLinkRules,
    showDigitsLatin: true,
    errorMessages: {
      [GeneralErrors.RegexMatch]:
        strings.dialog_ad_form.info_step.invalid_link_error,
      [GeneralErrors.ExcludeWithRegex]:
        strings.dialog_ad_form.info_step.join_link_error,
      [GeneralErrors.Required]: strings.common.error.required,
      [LinkErrors.ForbiddenPlatform]:
        strings.dialog_ad_form.info_step.errors.forbiddenPlatforms,
      [LinkErrors.WrongBaleUrl]:
        strings.dialog_ad_form.info_step.invalid_link_error,
    },
  });

  const isJoinLink = useMemo<boolean>(() => {
    return buttonLinkInputHandler.rawValue.includes("ble.ir/join");
  }, [buttonLinkInputHandler]);

  const adTextInputHandler = useInputHandler({
    initialValue: description,
    rules: [GeneralValidators.required, GeneralValidators.maxLength(200)],
  });

  const [linkError, setLinkError] = useState<string | undefined>("");

  useEffect(() => {
    const totalLinks = countLinks(adTextInputHandler.rawValue);
    if (totalLinks > 0) {
      setLinkError(strings.order.sponsored.info.errors.cant_have_link);
    } else {
      setLinkError(undefined);
    }
  }, [adTextInputHandler.rawValue]);

  const isDisable = useMemo<boolean>(() => {
    if (
      buttonLinkInputHandler.error ||
      adTextInputHandler.error ||
      linkError ||
      buttonTitleInputHandler.error
    ) {
      return true;
    }
    if (isJoinLink) {
      return (
        buttonLinkInputHandler.rawValue === "" ||
        adTextInputHandler.rawValue === ""
      );
    } else {
      return (
        buttonLinkInputHandler.rawValue === "" ||
        buttonTitleInputHandler.rawValue === "" ||
        adTextInputHandler.rawValue === ""
      );
    }
  }, [
    buttonLinkInputHandler,
    buttonTitleInputHandler,
    adTextInputHandler,
    isJoinLink,
  ]);

  return (
    <>
      <div className={styles.Content}>
        <CardWrapper className={styles.Card}>
          <NewTextField
            {...adTitleInputHandler}
            label={strings.order.sponsored.info.label}
            helpText={strings.order.sponsored.info.help}
            direction={"rtl"}
            placeholder={strings.order.sponsored.info.placeholder}
          />
          <NewTextField
            {...buttonLinkInputHandler}
            placeholder={strings.order.sponsored.info.button_link_placeholder}
            label={strings.order.sponsored.info.button_link}
            direction="rtl"
            helpText={strings.order.sponsored.info.button_link_helper}
          />
          <NewTextField
            {...buttonTitleInputHandler}
            value={
              isJoinLink
                ? strings.order.sponsored.info.join
                : buttonTitleInputHandler.value
            }
            placeholder={strings.order.sponsored.info.button_title_placeholer}
            disabled={isJoinLink}
            label={strings.order.sponsored.info.button_title}
            direction="rtl"
            helpText={
              isJoinLink
                ? strings.sponsored_messages.errors.join_link
                : strings.order.sponsored.info.button_title_helper
            }
            maxLength={15}
            showMaxLength={true}
            errorText={isJoinLink ? undefined : buttonTitleInputHandler.error}
          />
          <NewTextField
            multiline
            maxLength={200}
            {...adTextInputHandler}
            errorText={adTextInputHandler.error || linkError}
            direction="rtl"
            placeholder={strings.order.sponsored.info.ad_text_placeholder}
            showMaxLength={true}
            label={strings.order.sponsored.info.ad_text}
          />
        </CardWrapper>
      </div>
      <Footer>
        <NewButton
          disabled={isDisable}
          onClick={onSubmit}
          className={styles.Button}
          size={NewButtonSize.LARGE}
          loading={loading}
          data-testid="confirm-button"
        >
          {strings.common.button.submit_and_continue}
        </NewButton>
      </Footer>
    </>
  );
};
